import { useCallback, useMemo } from 'react';
import { PropTypes } from 'prop-types';
import { Editable, withReact, useSlate, Slate } from 'slate-react';
import {
  Editor,
  Transforms,
  createEditor,
  Element as SlateElement,
} from 'slate';

import { Button, Icon, Toolbar } from './components';
import { INITIAL_VALUE, deserialize, serialize } from './text_editor_config';
import './text_editor.scss';

const LIST_TYPES = ['numbered-list', 'bulleted-list'];
const TEXT_ALIGN_TYPES = ['left', 'center', 'right'];

const TextEditor = ({
  label,
  valid,
  value,
  disabled = false,
  className,
  onChange,
}) => {
  const renderElement = useCallback((props) => <Element {...props} />, []);
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);

  const editor = useMemo(() => withReact(createEditor()), []);
  let parsedValue = INITIAL_VALUE;

  if (value) {
    if (typeof value === 'object') {
      parsedValue = value;
    } else {
      const document = new DOMParser().parseFromString(value, 'text/html');
      parsedValue = deserialize(document.body);
    }
  }

  return (
    <div className="input-group">
      <label>
        {label}
        {!valid && <span className={className}>This field is required!</span>}
      </label>
      <div className={disabled ? 'rich-text disabled' : 'rich-text'}>
        <Slate
          editor={editor}
          value={parsedValue}
          onChange={(newValue) => {
            const html = newValue.map((n) => serialize(n)).join('');
            onChange(html);
          }}
        >
          <Toolbar className="toolbar">
            <MarkButton format="bold" icon="bold" disabled={disabled} />
            <MarkButton format="italic" icon="italic" disabled={disabled} />
            <MarkButton
              format="underline"
              icon="underlined"
              disabled={disabled}
            />
            <BlockButton
              format="heading-one"
              icon="heading-one"
              disabled={disabled}
            />
            <BlockButton
              format="heading-two"
              icon="heading-two"
              disabled={disabled}
            />
            <BlockButton
              format="numbered-list"
              icon="numbered-list"
              disabled={disabled}
            />
            <BlockButton
              format="bulleted-list"
              icon="bulleted-list"
              disabled={disabled}
            />
          </Toolbar>

          <Editable
            readOnly={disabled}
            className="body"
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            spellCheck
          />
        </Slate>
      </div>
    </div>
  );
};

const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type',
  );
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  });
  let newProperties;
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    };
  } else {
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    };
  }
  Transforms.setNodes(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const isBlockActive = (editor, format, blockType = 'type') => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n[blockType] === format,
    }),
  );

  return !!match;
};

const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

const Element = ({ attributes, children, element }) => {
  switch (element.type) {
    case 'block-quote':
      return <blockquote {...attributes}>{children}</blockquote>;
    case 'bulleted-list':
      return <ul {...attributes}>{children}</ul>;
    case 'heading-one':
      return <h1 {...attributes}>{children}</h1>;
    case 'heading-two':
      return <h2 {...attributes}>{children}</h2>;
    case 'list-item':
      return <li {...attributes}>{children}</li>;
    case 'numbered-list':
      return <ol {...attributes}>{children}</ol>;
    default:
      return <p {...attributes}>{children}</p>;
  }
};

const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong {...attributes}>{children}</strong>;
  }

  if (leaf.code) {
    children = <code {...attributes}>{children}</code>;
  }

  if (leaf.italic) {
    children = <em {...attributes}>{children}</em>;
  }

  if (leaf.underline) {
    children = <u {...attributes}>{children}</u>;
  }

  return <span {...attributes}>{children}</span>;
};

const BlockButton = ({ format, disabled, icon }) => {
  const editor = useSlate();
  return (
    <Button
      active={isBlockActive(
        editor,
        format,
        TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type',
      )}
      onClick={(event) => {
        if (!disabled) {
          event.preventDefault();
          toggleBlock(editor, format);
        }
      }}
      className="item editor-button"
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

const MarkButton = ({ format, disabled, icon }) => {
  const editor = useSlate();
  return (
    <Button
      active={isMarkActive(editor, format)}
      onClick={(event) => {
        if (!disabled) {
          event.preventDefault();
          toggleMark(editor, format);
        }
      }}
      className="item editor-button"
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};

TextEditor.propTypes = {
  label: PropTypes.string,
  valid: PropTypes.bool,
  value: PropTypes.string,
  className: PropTypes.string,
  onChange: PropTypes.func,
};

export default TextEditor;
